import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { toast } from '@/lib/toasts/api'
import { TableProvider, useTable } from '@/modules/drivers/context/TableContext'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableCheckbox from '@/modules/drivers/shared/tableItems/TableCheckbox'
import ChartDetailsActionBar from '@/modules/drivers/shared/actionBars/ChartDetailsActionBar'

import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import { ShowChartModal } from '@/modules/imports/container/scan-discs-popup-container'
import {
  ChartT,
  useChartT,
} from '@/modules/imports/container/scan-discs-popup-container/i18n'
import { TACHOGRAPH_STATUSES } from '@/modules/imports/container/scan-discs-popup-container/tachographStatus'
import {
  useDeleteChartDetails,
  useDriverChartDetails,
} from '@/lib/api/company/chartDetails/hooks'
import {
  DayType,
  DriverChartDetailsRow,
  TachographStatus,
  TachographType,
} from '@/lib/api/company/chartDetails/types'
import { FaIcon, faPenToSquareSolid, faTrashSolid } from '@/lib/fa'
import { Option } from '@/modules/settings/components/FieldsetSelect'

const ALL = -1
const ALL_VEHICLE = '__all__'

type Translate = (key: string) => string

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('pl-PL') : '—'

const toLocalDay = (iso: string) => {
  const d = new Date(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`
}

const fmtTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleTimeString('pl-PL', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—'

const dayTypeLabel = (t: Translate, type: DayType) => {
  switch (type) {
    case DayType.P:
      return t('driverModule.chart-day-type-work')
    case DayType.W:
      return t('driverModule.chart-day-type-off')
    default:
      return t('driverModule.chart-day-type-none')
  }
}

const tachographTypeLabel = (t: Translate, type: TachographType) =>
  type === TachographType.Digital
    ? t('driverModule.chart-data-digital')
    : t('driverModule.chart-data-analog')

const statusLabel = (T: ChartT, s: TachographStatus) => {
  const status = TACHOGRAPH_STATUSES.find((x) => x.value === s)
  return status ? T(status.key, status.def) : String(s)
}

const isAnalog = (r: DriverChartDetailsRow) =>
  r.tachographType === TachographType.Analog && r.tachographChartId > 0

type Row = DriverChartDetailsRow & { rowKey: string }

const withRowKeys = (rows: DriverChartDetailsRow[]): Row[] => {
  const seen = new Map<string, number>()
  return rows.map((r) => {
    const base = `${r.driverId}|${toLocalDay(r.chartDate)}|${r.tachographType}|${
      r.tachographChartId
    }|${r.vehicle ?? ''}`
    const occurrence = seen.get(base) ?? 0
    seen.set(base, occurrence + 1)
    return { ...r, rowKey: occurrence === 0 ? base : `${base}#${occurrence}` }
  })
}

type Props = {
  companyId: string
  driverIds: number[]
  startTime: string
  endTime: string
  refreshTrigger?: number
}

const ChartDetailsTableInner = ({
  companyId,
  driverIds,
  startTime,
  endTime,
  refreshTrigger = 0,
}: Props) => {
  const navigate = useNavigate()
  const { t } = useLocalize()
  const T = useChartT()
  const confirm = useConfirm()
  const { selectedGroup, setAllIds, toggleId, toggleAll, resetAll } = useTable()

  const {
    data: rows = [],
    isLoading,
    isError,
    refetch,
  } = useDriverChartDetails(companyId, driverIds, startTime, endTime)
  const del = useDeleteChartDetails(companyId)

  const [fDayType, setFDayType] = React.useState<number>(ALL)
  const [fType, setFType] = React.useState<number>(ALL)
  const [fStatus, setFStatus] = React.useState<number>(ALL)
  const [fVehicle, setFVehicle] = React.useState<string>(ALL_VEHICLE)

  const [editChartId, setEditChartId] = React.useState<number | null>(null)

  // The backend scopes analog charts by activities overlapping the range, so a
  // chart can come back with its "Dzień" outside it - trim to the picked range.
  const inRange = React.useMemo(
    () =>
      withRowKeys(
        rows.filter((r) => {
          const day = toLocalDay(r.chartDate)
          return day >= startTime && day <= endTime
        }),
      ),
    [rows, startTime, endTime],
  )

  const vehicles = React.useMemo(
    () =>
      Array.from(new Set(inRange.map((r) => r.vehicle).filter(Boolean))).sort(
        (a, b) => a.localeCompare(b),
      ),
    [inRange],
  )

  const statuses = React.useMemo(
    () =>
      Array.from(new Set(inRange.map((r) => r.tachographStatus))).sort(
        (a, b) => a - b,
      ),
    [inRange],
  )

  const filtered = React.useMemo(
    () =>
      inRange.filter(
        (r) =>
          (fDayType === ALL || r.dayType === fDayType) &&
          (fType === ALL || r.tachographType === fType) &&
          (fStatus === ALL || r.tachographStatus === fStatus) &&
          (fVehicle === ALL_VEHICLE || r.vehicle === fVehicle),
      ),
    [inRange, fDayType, fType, fStatus, fVehicle],
  )

  const selectableIds = React.useMemo(
    () => filtered.map((r) => r.rowKey),
    [filtered],
  )
  const selectableKey = selectableIds.join(',')
  React.useEffect(() => {
    setAllIds(selectableIds.map((id) => ({ id })))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableKey])

  const columns = React.useMemo<TableHeaderColumn[]>(
    () => [
      {
        title: t('driverModule.chart-col-driver'),
        key: 'driverFullName',
        type: 'string',
        sort: true,
      },
      {
        title: t('driverModule.chart-col-day'),
        key: 'chartDate',
        type: 'date',
        sort: true,
      },
      {
        title: t('driverModule.chart-col-start'),
        key: 'dayStartDate',
        type: 'date',
        sort: true,
      },
      {
        title: t('driverModule.chart-col-end'),
        key: 'dayEndDate',
        type: 'date',
        sort: true,
      },
      {
        title: t('driverModule.chart-col-vehicle'),
        key: 'vehicle',
        type: 'string',
        sort: true,
      },
      {
        title: t('driverModule.chart-col-day-type'),
        key: 'dayType',
        type: 'number',
        sort: true,
      },
      {
        title: t('driverModule.chart-col-crew'),
        key: 'isCrew',
        type: 'string',
        sort: false,
      },
      {
        title: t('driverModule.chart-col-data'),
        key: 'tachographType',
        type: 'number',
        sort: true,
      },
      {
        title: t('driverModule.chart-col-odometer-start'),
        key: 'vehicleOdometerBegin',
        type: 'number',
        sort: true,
      },
      {
        title: t('driverModule.chart-col-odometer-end'),
        key: 'vehicleOdometerEnd',
        type: 'number',
        sort: true,
      },
      {
        title: t('driverModule.chart-col-distance'),
        key: 'distanceFromTachographChart',
        type: 'number',
        sort: true,
      },
      {
        title: t('driverModule.chart-col-closed'),
        key: 'isClosed',
        type: 'string',
        sort: false,
      },
      {
        title: t('driverModule.chart-col-status'),
        key: 'tachographStatus',
        type: 'number',
        sort: true,
      },
      {
        title: t('driverModule.chart-col-created'),
        key: 'createdDate',
        type: 'date',
        sort: true,
      },
      {
        title: t('driverModule.chart-col-modified'),
        key: 'modifiedDate',
        type: 'date',
        sort: true,
      },
      { title: '', key: 'actions', type: 'string', sort: false },
    ],
    [t],
  )

  const { sortedData, sortConfig, handleHeaderClick } = useSortableData({
    data: filtered,
    tableColumns: columns,
    includeActiveSorting: false,
  })

  React.useEffect(() => {
    if (refreshTrigger > 0) refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger])

  // Summary (WPF footer counts), over the filtered set.
  const summary = React.useMemo(() => {
    const distinct = (vals: (string | number)[]) => new Set(vals).size
    return {
      records: filtered.length,
      days: distinct(filtered.map((r) => toLocalDay(r.chartDate))),
      drivers: distinct(filtered.map((r) => r.driverFullName)),
      vehicles: distinct(
        filtered.filter((r) => r.vehicle).map((r) => r.vehicle),
      ),
      analog: filtered.filter((r) => r.tachographType === TachographType.Analog)
        .length,
      digital: filtered.filter(
        (r) => r.tachographType === TachographType.Digital,
      ).length,
    }
  }, [filtered])

  const selectedKeys = React.useMemo(() => {
    const visible = new Set<string>(selectableIds)
    return new Set(
      (selectedGroup?.ids ?? []).map(String).filter((id) => visible.has(id)),
    )
  }, [selectedGroup, selectableIds])
  const hasSelection = selectedKeys.size > 0

  const deleteItems = React.useMemo(
    () =>
      Array.from(
        new Map(
          filtered
            .filter((r) => isAnalog(r) && selectedKeys.has(r.rowKey))
            .map((r) => [
              r.tachographChartId,
              { chartId: r.tachographChartId, driverId: r.driverId },
            ]),
        ).values(),
      ),
    [filtered, selectedKeys],
  )

  const doDelete = async (items: { chartId: number; driverId: number }[]) => {
    if (items.length === 0) return
    const ok = await confirm({
      title: t('driverModule.chart-delete-selected'),
      message: t('driverModule.chart-delete-confirm'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
    })
    if (!ok) return
    try {
      await del.mutateAsync(items)
      resetAll()
      toast.success({
        text: t('driverModule.chart-delete-success').replace(
          '{0}',
          String(items.length),
        ),
      })
    } catch (e) {
      console.error('chart-details delete failed', e)
      toast.error({
        text: t('driverModule.chart-delete-error'),
      })
    }
  }

  const openDay = (r: Row) =>
    navigate(
      `/et/company/${companyId}/driverModule/activities/driver/${r.driverId}/dailyActivity/${toLocalDay(
        r.chartDate,
      )}`,
    )

  const exportCsv = () => {
    const headers = columns
      .filter((c) => c.key !== 'actions')
      .map((c) => c.title)
    const cell = (r: Row) => [
      r.driverFullName,
      fmtDate(r.chartDate),
      fmtTime(r.dayStartDate),
      fmtTime(r.dayEndDate),
      r.vehicle || '',
      dayTypeLabel(t, r.dayType),
      r.isCrew ? t('common.yes') : t('common.no'),
      tachographTypeLabel(t, r.tachographType),
      r.vehicleOdometerBegin || '',
      r.vehicleOdometerEnd || '',
      r.distanceFromTachographChart || '',
      r.isClosed == null ? '' : r.isClosed ? t('common.yes') : t('common.no'),
      statusLabel(T, r.tachographStatus),
      fmtDate(r.createdDate),
      fmtDate(r.modifiedDate),
    ]
    const esc = (v: unknown) => `"${String(v).replace(/"/g, '""')}"`
    const lines = [headers, ...filtered.map(cell)].map((row) =>
      row.map(esc).join(';'),
    )
    const blob = new Blob(['﻿' + lines.join('\r\n')], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Raport_kart_kierowcow_i_wykresowek_${startTime}_${endTime}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const allLabel = (col: string) =>
    `${t(col)}: ${t('driverModule.chart-filter-all')}`

  const dayTypeOptions: Option[] = [
    { value: String(ALL), label: allLabel('driverModule.chart-col-day-type') },
    { value: String(DayType.P), label: dayTypeLabel(t, DayType.P) },
    { value: String(DayType.W), label: dayTypeLabel(t, DayType.W) },
    { value: String(DayType.None), label: dayTypeLabel(t, DayType.None) },
  ]
  const typeOptions: Option[] = [
    { value: String(ALL), label: allLabel('driverModule.chart-col-data') },
    {
      value: String(TachographType.Analog),
      label: tachographTypeLabel(t, TachographType.Analog),
    },
    {
      value: String(TachographType.Digital),
      label: tachographTypeLabel(t, TachographType.Digital),
    },
  ]
  const statusOptions: Option[] = [
    { value: String(ALL), label: allLabel('driverModule.chart-col-status') },
    ...statuses.map((s) => ({ value: String(s), label: statusLabel(T, s) })),
  ]
  const vehicleOptions: Option[] = [
    { value: ALL_VEHICLE, label: allLabel('driverModule.chart-col-vehicle') },
    ...vehicles.map((v) => ({ value: v, label: v })),
  ]

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
      <ChartDetailsActionBar
        elevated={editChartId == null}
        filters={[
          {
            options: dayTypeOptions,
            value: String(fDayType),
            onChange: (v) => setFDayType(Number(v)),
          },
          {
            options: typeOptions,
            value: String(fType),
            onChange: (v) => setFType(Number(v)),
          },
          {
            options: statusOptions,
            value: String(fStatus),
            onChange: (v) => setFStatus(Number(v)),
            searchEnabled: true,
          },
          {
            options: vehicleOptions,
            value: fVehicle,
            onChange: (v) => setFVehicle(v),
            searchEnabled: true,
          },
        ]}
        selectedCount={selectedKeys.size}
        onDelete={() => doDelete(deleteItems)}
        deleteDisabled={del.isPending || deleteItems.length === 0}
        deleteBlockedText={
          hasSelection && deleteItems.length === 0
            ? t('driverModule.chart-delete-analog-only')
            : undefined
        }
        onClearSelection={() => resetAll()}
        onRefresh={() => refetch()}
        onExport={exportCsv}
        exportDisabled={filtered.length === 0}
      />

      {/* Table */}
      <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto">
        {isLoading ? (
          <div className="tw-grid tw-h-[400px] tw-place-items-center">
            <LoaderIcon className="tw-h-16 tw-w-16 tw-animate-spin tw-text-primary" />
          </div>
        ) : isError ? (
          <TableEmpty title={t('driverCalendar.no-data')} />
        ) : sortedData.length === 0 ? (
          <TableEmpty title={t('driverCalendar.no-data')} />
        ) : (
          <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <TableHeader
              opacity
              rounded={false}
              sticky
              columns={columns}
              toggleAll={(checked) => toggleAll(checked, false)}
              isSelectedAny={hasSelection}
              disabledToggle={selectableIds.length === 0}
              onHeaderClick={handleHeaderClick}
              sortConfig={sortConfig}
            />
            <tbody className="">
              {sortedData.map((r) => {
                const analog = isAnalog(r)
                const checked = selectedKeys.has(r.rowKey)

                return (
                  <tr
                    key={r.rowKey}
                    onClick={() => toggleId(r.rowKey, false)}
                    onDoubleClick={() => openDay(r)}
                    className={`notches-border--right tw-group tw-relative tw-w-fit tw-border-b hover:tw-bg-primary/10 ${
                      checked ? 'selected tw-bg-primary/10' : 'tw-bg-white'
                    }`}
                  >
                    <TableCheckbox
                      checked={checked}
                      disabled={false}
                      onChange={() => toggleId(r.rowKey, false)}
                    />
                    <TableTextRow>{r.driverFullName}</TableTextRow>
                    <TableTextRow>{fmtDate(r.chartDate)}</TableTextRow>
                    <TableTextRow>{fmtTime(r.dayStartDate)}</TableTextRow>
                    <TableTextRow>{fmtTime(r.dayEndDate)}</TableTextRow>
                    <TableTextRow>{r.vehicle || '—'}</TableTextRow>
                    <TableTextRow>{dayTypeLabel(t, r.dayType)}</TableTextRow>
                    <TableTextRow>
                      {r.isCrew ? t('common.yes') : t('common.no')}
                    </TableTextRow>
                    <TableTextRow>
                      {tachographTypeLabel(t, r.tachographType)}
                    </TableTextRow>
                    <TableTextRow>{r.vehicleOdometerBegin || '—'}</TableTextRow>
                    <TableTextRow>{r.vehicleOdometerEnd || '—'}</TableTextRow>
                    <TableTextRow>
                      {r.distanceFromTachographChart || '—'}
                    </TableTextRow>
                    <TableTextRow>
                      {r.isClosed == null
                        ? '—'
                        : r.isClosed
                          ? t('common.yes')
                          : t('common.no')}
                    </TableTextRow>
                    <TableTextRow>
                      {statusLabel(T, r.tachographStatus)}
                    </TableTextRow>
                    <TableTextRow>{fmtDate(r.createdDate)}</TableTextRow>
                    <TableTextRow>{fmtDate(r.modifiedDate)}</TableTextRow>
                    <td className="tw-px-2">
                      <div className="tw-flex tw-items-center tw-gap-2 tw-opacity-0 group-hover:tw-opacity-100">
                        {analog && (
                          <>
                            <button
                              title={t('driverModule.chart-edit-chart')}
                              onClick={(e) => {
                                e.stopPropagation()
                                setEditChartId(r.tachographChartId)
                              }}
                              className="tw-text-primary hover:tw-opacity-70"
                            >
                              <FaIcon
                                icon={faPenToSquareSolid}
                                className="tw-h-3.5 tw-w-3.5"
                              />
                            </button>
                            <button
                              title={t('common.delete')}
                              onClick={(e) => {
                                e.stopPropagation()
                                doDelete([
                                  {
                                    chartId: r.tachographChartId,
                                    driverId: r.driverId,
                                  },
                                ])
                              }}
                              className="tw-text-red-600 hover:tw-opacity-70"
                            >
                              <FaIcon
                                icon={faTrashSolid}
                                className="tw-h-3.5 tw-w-3.5"
                              />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Summary footer (WPF counts) */}
      {!isLoading && sortedData.length > 0 && (
        <div className="tw-text-content-muted tw-flex tw-flex-wrap tw-gap-x-5 tw-gap-y-1 tw-border-t tw-border-base-300 tw-px-4 tw-py-1.5 tw-text-xs">
          <span>
            {t('driverModule.chart-sum-records')}: <b>{summary.records}</b>
          </span>
          <span>
            {t('driverModule.chart-sum-days')}: <b>{summary.days}</b>
          </span>
          <span>
            {t('driverModule.chart-sum-drivers')}: <b>{summary.drivers}</b>
          </span>
          <span>
            {t('driverModule.chart-sum-vehicles')}: <b>{summary.vehicles}</b>
          </span>
          <span>
            {t('driverModule.chart-sum-analog')}: <b>{summary.analog}</b>
          </span>
          <span>
            {t('driverModule.chart-sum-digital')}: <b>{summary.digital}</b>
          </span>
        </div>
      )}

      <ShowChartModal
        companyId={companyId}
        discId={editChartId}
        isOpen={editChartId != null}
        onClose={() => setEditChartId(null)}
        onSaved={() => refetch()}
      />
    </div>
  )
}

const ChartDetailsTable = (props: Props) => (
  <TableProvider bulk>
    <ChartDetailsTableInner {...props} />
  </TableProvider>
)

export default ChartDetailsTable
